import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    HStack,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
    Text,
    Badge,
} from "@chakra-ui/react";
import { useGoogleMaps } from "../DeliveryMap/useGoogleMaps";
import { initSocket } from "../../initSocket";

type LatLng = { lat: number; lng: number; };

// --- helpers ---
function decodePolyline(encoded: string): LatLng[] {
    const points: LatLng[] = [];
    let index = 0, lat = 0, lng = 0;
    while (index < encoded.length) {
        let b: number, shift = 0, result = 0;
        do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
        const dlat = (result & 1) ? ~(result >> 1) : (result >> 1); lat += dlat;
        shift = 0; result = 0;
        do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
        const dlng = (result & 1) ? ~(result >> 1) : (result >> 1); lng += dlng;
        points.push({ lat: lat / 1e5, lng: lng / 1e5 });
    }
    return points;
}

// Haversine meters
function haversine(a: LatLng, b: LatLng) {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const R = 6371e3;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const s1 = toRad(a.lat), s2 = toRad(b.lat);
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(s1) * Math.cos(s2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x));
}

function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }
function lerpPoint(a: LatLng, b: LatLng, t: number): LatLng {
    return { lat: lerp(a.lat, b.lat, t), lng: lerp(a.lng, b.lng, t) };
}

function buildStraightPath(pickup: LatLng, dropoff: LatLng, points = 200): LatLng[] {
    const path: LatLng[] = [];
    for (let i = 0; i <= points; i++) {
        const t = i / points;
        path.push(lerpPoint(pickup, dropoff, t));
    }
    return path;
}

export default function DriverSimulatorOnRoute({
    tripId,
    pickup,
    dropoff,
    startKph = 35,
}: {
    tripId: string;
    pickup: LatLng;
    dropoff: LatLng;
    startKph?: number;
}) {
    const { isLoaded, loadError } = useGoogleMaps();
    const [status, setStatus] = useState("Idle");
    const [speedKph, setSpeedKph] = useState(startKph);
    const [running, setRunning] = useState(false);

    const routeRef = useRef<LatLng[]>([]);
    const segLensRef = useRef<number[]>([]);     // meters per segment
    const totalLenRef = useRef<number>(0);
    const distAlongRef = useRef<number>(0);      // meters from start
    const rafRef = useRef<number>(0);
    const lastTsRef = useRef<number>(0);
    const lastEmitRef = useRef<number>(0);
    const lastEmittedPosRef = useRef<LatLng | null>(null);
    const socketRef = useRef<any>(null);

    // 1) Build route once: Google route OR straight fallback
    useEffect(() => {
        if (!isLoaded) return;

        const finalizeRoute = (pts: LatLng[], origin: "google" | "fallback") => {
            if (pts.length < 2) {
                console.warn("❌ Route points < 2, forcing fallback");
                pts = buildStraightPath(pickup, dropoff);
                origin = "fallback";
            }
            routeRef.current = pts;

            // Precompute segment lengths and total
            let tot = 0;
            const segs: number[] = [];
            for (let i = 0; i < pts.length - 1; i++) {
                const m = haversine(pts[i], pts[i + 1]);
                segs.push(m);
                tot += m;
            }
            segLensRef.current = segs;
            totalLenRef.current = tot;
            distAlongRef.current = 0;

            setStatus(
                `${origin === "google" ? "Route ready" : "Fallback route"} (${Math.round(
                    tot / 1000
                )} km, ${pts.length} pts)`
            );
            console.log(`🛣️ ${origin} route ready:`, { points: pts.length, km: Math.round(tot / 1000) });
        };

        const svc = new google.maps.DirectionsService();
        svc.route(
            { origin: pickup, destination: dropoff, travelMode: google.maps.TravelMode.DRIVING },
            (res, st) => {
                if (st === google.maps.DirectionsStatus.OK && res) {
                    const polyRaw: unknown = res.routes[0]?.overview_polyline as unknown;
                    let encoded: string | undefined;
                    if (typeof polyRaw === "string") encoded = polyRaw;
                    else if (polyRaw && typeof (polyRaw as any).points === "string") encoded = (polyRaw as any).points;

                    if (encoded) {
                        const pts = decodePolyline(encoded);
                        finalizeRoute(pts, "google");
                    } else {
                        console.warn("❌ Directions without polyline → fallback");
                        finalizeRoute(buildStraightPath(pickup, dropoff), "fallback");
                    }
                } else {
                    console.warn("❌ Directions failed:", st, "→ fallback");
                    finalizeRoute(buildStraightPath(pickup, dropoff), "fallback");
                }
            }
        );
    }, [isLoaded, pickup, dropoff]);

    // 2) Open socket & join on connect
    useEffect(() => {
        (async () => {
            const socket = await initSocket();
            socketRef.current = socket;

            const join = () => {
                console.log("🚚 simulator connected:", socket.id, "→ joining room", tripId);
                socket.emit("joinTripRoom", { tripId });
            };
            if (socket.connected) join();
            socket.on("connect", join);

            socket.on("joinedTripRoom", (ack) => console.log("✅ simulator joined ACK:", ack));
            socket.on("locationUpdate", (p) => console.log("🔁 simulator echo:", p));
        })();
    }, [tripId]);

    // 3) Simulation tick — advance along route at chosen speed
    const step = (ts: number) => {
        const route = routeRef.current;
        if (!route.length || !running) { rafRef.current = requestAnimationFrame(step); return; }

        const last = lastTsRef.current || ts;
        const dt = (ts - last) / 1000; // seconds
        lastTsRef.current = ts;

        const speedMps = (speedKph * 1000) / 3600;
        distAlongRef.current += speedMps * dt;

        const total = totalLenRef.current;
        if (total <= 0) { rafRef.current = requestAnimationFrame(step); return; }

        // loop (nice for demo)
        if (distAlongRef.current > total) distAlongRef.current -= total;

        // find segment for current distance
        let d = distAlongRef.current;
        let idx = 0;
        const segs = segLensRef.current;
        while (idx < segs.length && d > segs[idx]) {
            d -= segs[idx];
            idx++;
        }
        const a = route[idx];
        const b = route[idx + 1] ?? route[route.length - 1];
        const segLen = segs[idx] || 1;
        const t = Math.min(1, Math.max(0, d / segLen));
        const pos = lerpPoint(a, b, t);

        // ✅ Emit when moved ≥ 15 m OR ≥ 1s since last emit
        const since = ts - lastEmitRef.current;
        const moved = lastEmittedPosRef.current ? haversine(lastEmittedPosRef.current, pos) : Infinity;
        if (moved >= 15 || since >= 1000) {
            console.log("📤 EMIT locationUpdate", {
                tripId, lat: pos.lat, lng: pos.lng, speed: speedKph,
                moved: Math.round(moved), idx, t: Number(t.toFixed(2))
            });
            socketRef.current?.emit("locationUpdate", { tripId, lat: pos.lat, lng: pos.lng, speed: speedKph });
            lastEmitRef.current = ts;
            lastEmittedPosRef.current = pos;
        }

        rafRef.current = requestAnimationFrame(step);
    };

    const start = () => {
        if (!routeRef.current.length) {
            console.warn("❌ start(): route not ready, using fallback straight path");
            // force fallback immediately if something went wrong
            routeRef.current = buildStraightPath(pickup, dropoff);
            segLensRef.current = routeRef.current.slice(0, -1).map((p, i) => haversine(p, routeRef.current[i + 1]));
            totalLenRef.current = segLensRef.current.reduce((a, b) => a + b, 0);
        }

        console.log("▶️ start simulation at", speedKph, "kph");
        setRunning(true);
        setStatus("Simulating on route…");
        distAlongRef.current = 0;
        lastTsRef.current = performance.now();

        // First immediate emit
        const first = routeRef.current[0];
        if (first) {
            console.log("📤 EMIT initial locationUpdate", { tripId, ...first, speed: speedKph });
            socketRef.current?.emit("locationUpdate", { tripId, lat: first.lat, lng: first.lng, speed: speedKph });
            lastEmitRef.current = performance.now();
            lastEmittedPosRef.current = first;
        }

        rafRef.current = requestAnimationFrame(step);
    };

    const stop = () => {
        setRunning(false);
        setStatus("Stopped");
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };

    useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

    if (loadError) return <Box color="red.500">Maps failed to load.</Box>;
    if (!isLoaded) return <Box>Loading route…</Box>;

    return (
        <Box p={3} borderWidth="1px" borderRadius="8px">
            <HStack justify="space-between" mb={2}>
                <Badge colorScheme={running ? "green" : "gray"}>{status}</Badge>
                <HStack>
                    <Button size="sm" colorScheme="blue" onClick={start} isDisabled={running}>Start</Button>
                    <Button size="sm" onClick={stop} isDisabled={!running}>Stop</Button>
                </HStack>
            </HStack>
            <HStack>
                <Text fontSize="sm" w="90px">Speed: {speedKph} km/h</Text>
                <Slider min={5} max={80} step={1} value={speedKph} onChange={setSpeedKph}>
                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                    <SliderThumb />
                </Slider>
            </HStack>
        </Box>
    );
}
